import { NodeDefinition } from 'Automations/nodeDefinitions/types'
import { ActionType } from '..'
import { ActionEditorNode } from '../action.editorNode'
import { ActionCreateScheduleNodeConfigForm } from './actionCreateSchedule.configForm'
import { ActionIcon } from '../action.icon'
import { actionTypeRequired, handlesConnected } from '../action.validators'
import {
  createSimpleTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import {
  getClosestMatches,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import { CreateScheduleExecutionSummary } from './CreateSchedule.executionSummary'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export type Config = {
  name: string
  actionType: ActionType
}

type Inputs =
  | 'assetId'
  | 'customerId'
  | 'billingSchedule'
  | 'requestorId'
  | 'contractName'
  | 'assetFileName'

type TaskName = 'createSchedule' | 'sendEmail'

const taskReferenceBuilder = getTaskReferenceName<TaskName>(
  'ACTION_CREATE_SCHEDULE'
)

export const actionCreateScheduleNodeDefinition: NodeDefinition<
  Config,
  Inputs
> = {
  name: 'Action',
  description: 'Create schedule',
  editorNode: ActionEditorNode,
  configForm: ActionCreateScheduleNodeConfigForm,
  icon: ActionIcon,
  defaultConfig: {
    name: '',
    actionType: 'CREATE_SCHEDULE'
  },
  validation: {
    node: { ERROR: [], WARN: [] },
    config: {
      name: { ERROR: [], WARN: [] },
      actionType: { ERROR: [actionTypeRequired], WARN: [] }
    },
    handles: { ERROR: [handlesConnected], WARN: [] }
  },
  inputMapping: ({ upstreamNodes }) => {
    return getClosestMatches<Inputs>(upstreamNodes)({
      assetId: semanticTypes.sequence.asset.id,
      customerId: semanticTypes.sequence.customer.id,
      billingSchedule: semanticTypes.sequence.billingSchedule,
      requestorId: semanticTypes.sequence.requestorId,
      contractName: semanticTypes.sequence.contract.name,
      assetFileName: semanticTypes.sequence.asset.fileName
    })
  },
  taskTransformer: ({ nodeId, inputMapping }) => {
    const taskReferenceName = taskReferenceBuilder(nodeId)

    return [
      createSimpleTask({
        name: 'update_sequence_data',
        taskReferenceName: taskReferenceName('createSchedule'),
        inputParameters: {
          taskExecutorConfiguration: {
            version: 1,
            executor: TASK_EXECUTORS.createSchedule
          },
          data: wrapVariables({
            assetId: inputMapping.assetId,
            customerId: inputMapping.customerId,
            billingSchedule: inputMapping.billingSchedule,
            requestorId: inputMapping.requestorId,
            contractName: inputMapping.contractName,
            assetFileName: inputMapping.assetFileName
          })
        }
      }),
      createSimpleTask({
        name: 'update_sequence_data',
        taskReferenceName: taskReferenceName('sendEmail'),
        inputParameters: {
          taskExecutorConfiguration: {
            executor: TASK_EXECUTORS.sendEmail,
            version: 1
          },
          data: {
            ...wrapVariables({
              contractName: inputMapping.contractName,
              assetFilename: inputMapping.assetFileName,
              billingScheduleId: `${taskReferenceName('createSchedule')}.output.billingScheduleId`,
              requestorId: inputMapping.requestorId
            }),
            emailType: 'AUTOMATIONS_SCHEDULE_CREATED'
          }
        }
      })
    ]
  },
  executionSummary: CreateScheduleExecutionSummary
}
